/**
 * Online Training modules (CEO, 2026-11-02) — the wire shapes portal-api's
 * `/api/v1/admin/training_modules*` + `/training_topics*` answer with, as the
 * dashboard server relays them to the command center's authoring page.
 *
 * A MODULE is one service or extension (Mass Notification, Audio
 * Conferencing, …). Its menu is exactly TWO levels: a top-level topic is a
 * SECTION and the topics under it are LESSONS. Every topic is one short video;
 * a section may carry a video of its own. Full contract:
 * `docs/online-training.md`.
 */

export type TrainingModuleCategory = "service" | "extension";

export const TRAINING_MODULE_CATEGORIES: readonly TrainingModuleCategory[] = [
  "service",
  "extension",
] as const;

/** A topic's video — a pasted link, or an uploaded file. */
export type TrainingVideo =
  | { kind: "link"; url: string }
  | {
      kind: "file";
      filename: string;
      content_type: string;
      byte_size: number;
      /**
       * portal-api answers with a signed `/rails/active_storage` path. The
       * dashboard server REPLACES it with its own staff relay
       * (`trainingVideoRelayPath`) before the body reaches a browser, so the
       * signed upstream address never leaves the server.
       */
      preview_url: string;
    };

export interface TrainingTopic {
  id: number;
  parent_id: number | null;
  title: string;
  summary: string | null;
  position: number;
  duration_seconds: number | null;
  video: TrainingVideo | null;
}

export interface TrainingSection extends TrainingTopic {
  lessons: TrainingTopic[];
}

export interface TrainingModuleSummary {
  id: number;
  slug: string;
  title: string;
  summary: string | null;
  category: TrainingModuleCategory;
  position: number;
  published: boolean;
  /** Topics WITH a video. */
  topic_count: number;
  total_duration_seconds: number;
  updated_at: string;
}

export interface TrainingModuleDetail extends TrainingModuleSummary {
  sections: TrainingSection[];
  /** Topic ids that have a video, in play order. */
  playlist: number[];
}

export interface TrainingModulePayload {
  title?: string;
  summary?: string | null;
  category?: TrainingModuleCategory;
  published?: boolean;
  position?: number;
}

export interface TrainingTopicPayload {
  title?: string;
  summary?: string | null;
  parent_id?: number | null;
  /** `""` clears a link. Setting one purges an uploaded file upstream. */
  video_url?: string;
  duration_seconds?: number | null;
}

/** One row of the whole-menu reorder (`PUT …/reorder`). */
export interface TrainingReorderRow {
  id: number;
  parent_id: number | null;
  position: number;
}

/** The upload ceiling portal-api enforces (`VIDEO_TOO_LARGE` above it). */
export const TRAINING_VIDEO_MAX_BYTES = 50 * 1024 * 1024;

/**
 * Where the command center streams an uploaded topic video from: the
 * dashboard server's staff relay. Relative to the dashboard API origin.
 * The module is named because the relay resolves the file from the
 * module's detail — never from a client-supplied URL.
 */
export function trainingVideoRelayPath(topicId: number, moduleId: number | string): string {
  return `/api/documentation/training-topics/${encodeURIComponent(String(topicId))}/video?module=${encodeURIComponent(String(moduleId))}`;
}
